import { ArrowRight, Plus, RotateCcw } from 'lucide-react';
import { IncompleteBadge } from './IncompleteBadge';
import { SessionStatCard } from './SessionStatCard';
import { Button } from '@ai-tutor/ui';
import type { SessionRecord } from '@/hooks/mindspace/usePracticeHistory';
import { OutcomeAuditList } from '@/components/features/mindspace/practice/session/OutcomeAuditList';
import {
  formatDuration,
  formatRelativeTime,
  scopeIcon,
  scopeLabel,
} from '@/components/features/mindspace/practice/utils';

interface SessionDetailViewProps {
  record: SessionRecord;
  onPracticeWeak: (conceptIds: string[]) => void;
  onPracticeAll: (conceptIds: string[]) => void;
  onNewSession: () => void;
}

export function SessionDetailView({
  record,
  onPracticeWeak,
  onPracticeAll,
  onNewSession,
}: SessionDetailViewProps) {
  const Icon = scopeIcon(record.scope);
  const hasOutcomes = record.outcomes.length > 0;
  const weakOutcomes = record.outcomes.filter(o => o.grade <= 2);
  const allConceptIds = record.outcomes.map(o => o.conceptId);
  const weakConceptIds = weakOutcomes.map(o => o.conceptId);

  return (
    <div className='mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-6 sm:py-8'>
      {/* Header */}
      <header>
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Icon className='h-4 w-4 text-brand-500 dark:text-primary' />
          <span className='font-medium text-foreground'>
            {scopeLabel(record.scope)}
          </span>
          <span>·</span>
          <span>{record.mode === 'flashcard' ? 'Flashcards' : 'Quiz'}</span>
          <span>·</span>
          <span>{formatRelativeTime(record.endedAt)}</span>
          {record.incomplete && <IncompleteBadge className='ml-1' />}
        </div>
        <h2 className='mt-2 text-xl font-semibold text-foreground'>
          {record.incomplete
            ? `Practiced ${record.outcomes.length} of ${record.total} concept${record.total === 1 ? '' : 's'}`
            : `Practiced ${record.total} concept${record.total === 1 ? '' : 's'}`}
        </h2>
        <p className='mt-1 text-sm text-muted-foreground'>
          {formatDuration(record.durationMs)}
          {masterySuffix(record)}
        </p>
      </header>

      {/* Stat cards (only when we have grade breakdown) */}
      {hasOutcomes && (
        <div className='grid grid-cols-3 gap-3'>
          <SessionStatCard
            label='Mastered'
            value={record.mastered}
            tone='emerald'
          />
          <SessionStatCard
            label='Improved'
            value={record.improved}
            tone='indigo'
          />
          <SessionStatCard label='Weak' value={record.weak} tone='rose' />
        </div>
      )}

      {/* Expandable Q&A audit - clicking a row reveals the verbatim
          question(s) asked and what the user picked / saw. */}
      {hasOutcomes ? (
        <OutcomeAuditList outcomes={record.outcomes} />
      ) : (
        <div className='rounded-2xl border border-dashed border-border bg-muted/40 px-6 py-6 text-center dark:bg-white/[0.03]'>
          <p className='text-sm text-muted-foreground'>
            No per-concept results captured for this session.
          </p>
        </div>
      )}

      {/* CTAs. Full width and stacked on a phone, the main one first: wrapped
          and centred they came out at three different widths. */}
      <div className='flex flex-wrap items-center justify-center gap-2 max-sm:flex-col max-sm:items-stretch [&>button]:max-sm:h-11'>
        {weakConceptIds.length > 0 && (
          <Button
            onClick={() => onPracticeWeak(weakConceptIds)}
            className='gap-1'
          >
            Practice weak ({weakConceptIds.length})
            <ArrowRight className='h-4 w-4' />
          </Button>
        )}
        {allConceptIds.length > 0 && (
          <Button
            variant='outline'
            onClick={() => onPracticeAll(allConceptIds)}
            className='gap-1'
          >
            <RotateCcw className='h-3.5 w-3.5' />
            Practice all again
          </Button>
        )}
        <Button variant='outline' onClick={onNewSession} className='gap-1'>
          <Plus className='h-3.5 w-3.5' />
          New session
        </Button>
      </div>
    </div>
  );
}

/**
 * ` · N% mastered`, over the RATED concepts — empty when the record carries
 * no outcomes or nothing was rated. `total` is the deck size, which an
 * incomplete run never reached, so dividing by it under-reports how the rated
 * concepts went.
 */
function masterySuffix(record: SessionRecord): string {
  const rated = record.mastered + record.improved + record.weak;
  if (record.outcomes.length === 0 || rated === 0) return '';
  return ` · ${Math.round((record.mastered / rated) * 100)}% mastered`;
}
